import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useGovernmentOfOntarioFlag } from 'utils/featureFlags';
import {
  DEFAULT_ACTIVE_HURRICANES_SOURCE,
  DEFAULT_TROPICAL_CONDITIONS,
  useCreateOrgEventAutomationRule,
  useOrgEventAutomationRules,
  type CreateOrgEventAutomationRulePayload,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { NotificationType } from 'hooks/useNotification';
import {
  ONTARIO_GTA_AUTOMATION_RULE_NAME,
  ONTARIO_GTA_ICE_RULE_NAME,
  ONTARIO_GTA_INFRASTRUCTURE_RULE_NAME,
} from '../NotificationsTab/ontarioSnowstormNotification';

const ensureLocks = new Map<string, Promise<unknown>>();

const ONTARIO_SEEDED_RULES: Array<{
  name: string;
  type: string;
  thresholdDescription: string;
}> = [
  {
    name: ONTARIO_GTA_AUTOMATION_RULE_NAME,
    type: 'Winter Storm',
    thresholdDescription:
      'Winter storm warning and ≥ 1 Government of Ontario facility in the Greater Toronto Area',
  },
  {
    name: ONTARIO_GTA_ICE_RULE_NAME,
    type: 'Ice',
    thresholdDescription:
      'Ice risk to Government of Ontario GTA facilities',
  },
  {
    name: ONTARIO_GTA_INFRASTRUCTURE_RULE_NAME,
    type: 'Infrastructure',
    thresholdDescription:
      'Infrastructure disruption risk to Government of Ontario GTA facilities',
  },
];

/**
 * Ensures the Ontario demo group has winter-storm / ice / infrastructure rules
 * so resource checklists can be keyed by rule name.
 */
export function useEnsureOntarioWinterAutomationRule() {
  const ontarioEnabled = useGovernmentOfOntarioFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const { data: rules, isSuccess } = useOrgEventAutomationRules({
    enabled: ontarioEnabled && !!groupGuid,
  });
  const createRule = useCreateOrgEventAutomationRule();
  const attempted = useRef(false);

  useEffect(() => {
    if (!ontarioEnabled || !groupGuid || !isSuccess || attempted.current) return;
    const existingNames = new Set(
      (rules ?? []).filter((rule) => !rule.archived).map((rule) => rule.name)
    );
    const missing = ONTARIO_SEEDED_RULES.filter(
      (seed) => !existingNames.has(seed.name)
    );
    if (missing.length === 0) {
      attempted.current = true;
      return;
    }

    const lockKey = `ontario-automation-rules:${groupGuid}`;
    if (ensureLocks.has(lockKey)) return;

    attempted.current = true;
    const notifyConfig = {
      notificationTypes: [NotificationType.IN_APP],
      userIds: userGuid ? [userGuid] : [],
      teamIds: [],
      priority: 'watch',
      titleTemplate: 'Threshold event: {eventName}',
      bodyTemplate:
        '{eventName}: {stormName} impacts {resourceCount} resources.',
    };

    const promise = Promise.all(
      missing.map((seed) => {
        const payload: CreateOrgEventAutomationRulePayload = {
          group_guid: groupGuid,
          name: seed.name,
          enabled: false,
          logic: 'all',
          source: DEFAULT_ACTIVE_HURRICANES_SOURCE,
          conditions: DEFAULT_TROPICAL_CONDITIONS,
          event_template: {
            severity: 'High',
            type: seed.type,
            region: 'Greater Toronto Area',
            thresholdDescription: seed.thresholdDescription,
          },
          notify_on_create: false,
          checklist_on_create: false,
          checklist_config: { items: [] },
          notify_config: notifyConfig,
          created_by_user_guid: userGuid ?? null,
        };
        return createRule.mutateAsync(payload);
      })
    )
      .catch((err) => {
        console.warn('[ontario] failed to ensure automation rules', err);
        attempted.current = false;
      })
      .finally(() => {
        ensureLocks.delete(lockKey);
      });

    ensureLocks.set(lockKey, promise);
  }, [ontarioEnabled, groupGuid, userGuid, isSuccess, rules, createRule]);
}
